import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";

import { useSites } from "../lib/useSites";
import type { Site } from "../lib/types";
import { EuMap } from "../components/EuMap";
import { FilterChips, type Filters } from "../components/FilterChips";

const EMPTY_FILTERS: Filters = { country: null, bu: null, site_type: null };

export function Landing() {
  const sites = useSites();
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    const ql = q.toLowerCase().trim();
    return sites.filter(
      (s) =>
        (!filters.country || s.country === filters.country) &&
        (!filters.bu || s.bu === filters.bu) &&
        (!filters.site_type || s.site_type === filters.site_type) &&
        (!ql ||
          s.code.toLowerCase().includes(ql) ||
          (s.address ?? "").toLowerCase().includes(ql)),
    );
  }, [sites, filters, q]);

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-7xl">
      {/* Hero */}
      <header>
        <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-400">
          spreenauten · amzel · nest
        </div>
        <h1 className="text-2xl sm:text-3xl font-semibold mt-1">
          EU DMR Site Knowledge Base
        </h1>
        <p className="text-sm text-zinc-400 mt-2 max-w-3xl">
          <span className="text-zinc-200 font-medium">{sites.length} sites</span>{" "}
          across <span className="font-mono">8 countries</span> · hardware inventory,
          codeplugs, frequency allocations, AES vault, heatmaps, field photos, crew
          history. One source of truth for Amazon EU.
        </p>
      </header>

      {/* Map + filters */}
      <section className="grid lg:grid-cols-[1.5fr_1fr] gap-4">
        <div>
          <EuMap sites={filtered} />
        </div>
        <div className="space-y-4">
          <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3">
            <div className="flex items-center gap-2 border border-zinc-800 rounded bg-zinc-950/60 px-2">
              <Search className="h-3.5 w-3.5 text-zinc-500" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="filter sites by code or address…"
                className="flex-1 bg-transparent outline-none py-1.5 text-sm placeholder:text-zinc-600"
              />
              {q && (
                <button
                  type="button"
                  onClick={() => setQ("")}
                  className="text-xs text-zinc-500 hover:text-zinc-200"
                >
                  clear
                </button>
              )}
            </div>
          </div>
          <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3">
            <FilterChips sites={sites} filters={filters} onChange={setFilters} />
            {(filters.country || filters.bu || filters.site_type) && (
              <button
                type="button"
                onClick={() => setFilters(EMPTY_FILTERS)}
                className="mt-2 text-[10px] font-mono text-zinc-500 hover:text-zinc-200"
              >
                reset filters
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Site table */}
      <section>
        <div className="flex items-baseline justify-between mb-2">
          <h2 className="text-sm font-mono uppercase tracking-widest text-zinc-400">
            {filtered.length} of {sites.length} sites
          </h2>
          <div className="text-[10px] font-mono text-zinc-600">
            GET /api/v1/dossier/sites?country=… ↩
          </div>
        </div>
        <SiteTable sites={filtered} />
      </section>
    </div>
  );
}

function SiteTable({ sites }: { sites: Site[] }) {
  return (
    <div className="overflow-auto rounded-lg border border-zinc-800">
      <table className="w-full text-xs">
        <thead className="bg-zinc-900/60 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
          <tr>
            <Th>Code</Th>
            <Th>Country</Th>
            <Th>BU</Th>
            <Th>Type</Th>
            <Th>Repeaters</Th>
            <Th>Status</Th>
            <Th className="w-[40%]">Address</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-900">
          {sites.slice(0, 50).map((s) => (
            <tr key={s.code} className="hover:bg-zinc-900/40">
              <Td>
                <Link to={`/sites/${s.code}`} className="font-mono font-semibold text-emerald-300 hover:underline">
                  {s.code}
                </Link>
              </Td>
              <Td className="font-mono">{s.country}</Td>
              <Td className="font-mono text-zinc-400">{s.bu}</Td>
              <Td className="font-mono">
                <span
                  className={
                    s.site_type === "dmr"
                      ? "text-emerald-300"
                      : s.site_type === "pmr446"
                        ? "text-sky-300"
                        : s.site_type === "planned"
                          ? "text-amber-300"
                          : "text-zinc-500"
                  }
                >
                  {s.site_type}
                </span>
              </Td>
              <Td className="font-mono text-zinc-400">
                {s.repeaters > 0 ? s.repeaters + "x" : "—"}
              </Td>
              <Td className="font-mono text-zinc-400">{s.status}</Td>
              <Td className="text-zinc-500 truncate max-w-[420px]">{s.address ?? "—"}</Td>
            </tr>
          ))}
        </tbody>
      </table>
      {sites.length > 50 && (
        <div className="px-3 py-2 text-[11px] font-mono text-zinc-600 border-t border-zinc-800 bg-zinc-900/40">
          showing 50 of {sites.length} — pagination in phase 1
        </div>
      )}
    </div>
  );
}

function Th({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <th className={`text-left px-3 py-2 ${className}`}>{children}</th>;
}
function Td({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-3 py-1.5 ${className}`}>{children}</td>;
}
